import actionTypes from "./_actions";
import * as utils from "common/utils";
import authActions from "containers/Auth/_actions";
import { requiresOutdoorPigLiquidApplications, requiresOutdoorPigSolidApplications, requiresOutdoorPigStrawCompostApplications } from "common/viewModels";

const INITIAL_STATE = [];

function reducer(state = INITIAL_STATE, action) {
    if (action && action.type === authActions.AUTH_CLEAR_STORAGE) {
        return INITIAL_STATE;
    }

    if (utils.skipReducer(action, ["EFFLUENT_"])) {
        return state;
    }

    let offlineFarms = [...state];
    const offlineFarm = utils.getOfflineFarm(offlineFarms, action.payload.farmId);
    if (!offlineFarm) return state;
    const offlineBudget = utils.getOfflineBudget(offlineFarm, action.payload.budgetId);
    if (!offlineBudget) return state;

    switch (action.type) {
        case actionTypes.EFFLUENT_SYSTEM_SUBMIT:
            if (action.payload.structureId) {
                const structure = offlineBudget.budget.structures.find((s) => s.id === action.payload.structureId);
                structure.effluentSystem = action.payload.effluentSystem;
            } else {
                offlineBudget.budget.effluentSystem = action.payload.effluentSystem;
            }
            offlineBudget.isDirty = true;
            return offlineFarms;

        case actionTypes.EFFLUENT_SYSTEM_DELETE:
            offlineBudget.budget.effluentSystem = undefined;
            offlineBudget.isDirty = true;
            return offlineFarms;

        case actionTypes.EFFLUENT_SAVE_OUTDOOR_PIG_EFFLUENT_SYSTEM: {
            const { outdoorPigEffluentSystem } = action.payload;

            offlineBudget.budget.outdoorPigEffluentSystem = outdoorPigEffluentSystem;

            // Update villages effluent disposal.
            if (outdoorPigEffluentSystem.villagesEffluentDisposal) {
                offlineBudget.budget.structures = (offlineBudget.budget.structures || []).map((structure) => {
                    if (structure.type !== "OutdoorPigFarrowingVillages") return structure;

                    return {
                        ...structure,
                        effluentDisposal: outdoorPigEffluentSystem.villagesEffluentDisposal,
                    };
                });
            }

            // Update barns effluent disposal.
            if (outdoorPigEffluentSystem.barnsEffluentDisposal) {
                offlineBudget.budget.structures = (offlineBudget.budget.structures || []).map((structure) => {
                    if (structure.type !== "OutdoorPigBarns") return structure;

                    return {
                        ...structure,
                        effluentDisposal: outdoorPigEffluentSystem.barnsEffluentDisposal,
                    };
                });
            }

            offlineBudget.isDirty = true;
            return offlineFarms;
        }

        case actionTypes.EFFLUENT_STRUCTURE_SUBMIT: {
            const index = offlineBudget.budget.structures.findIndex((s) => s.id === action.payload.structure.id);
            if (index >= 0) offlineBudget.budget.structures.splice(index, 1, action.payload.structure);
            else offlineBudget.budget.structures.push(action.payload.structure);
            offlineBudget.isDirty = true;
            // Add as option to dairy goat left overs if structure is a wintering pad.
            if (["CoveredWinteringPad", "UncoveredWinteringPad"].includes(action.payload.structure.type)) {
                offlineBudget.budget.enterprises = (offlineBudget.budget.enterprises || []).map((enterprise) => {
                    if (enterprise.type === "DairyGoat" && enterprise.dairyGoatLeftOvers && enterprise.dairyGoatLeftOvers.winteringPadLeftOvers && enterprise.dairyGoatLeftOvers.winteringPadLeftOvers.fate === "FedOnWinteringPads") {
                        if (!enterprise.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages.some((ep) => ep.enterpriseId === action.payload.structure.enterpriseId)) {
                            enterprise.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages.push({ enterpriseId: action.payload.structure.enterpriseId, percentage: 0 });
                        }
                    }
                    return enterprise;
                });
            }
            // Clean up associated outdoor pigs effluent applications.
            if (offlineBudget.budget.outdoorPigEffluentSystem && ["OutdoorPigFarrowingVillages", "OutdoorPigBarns", "OutdoorPigHuts"].includes(action.payload.structure.type)) {
                if (!offlineBudget.budget.structures.some((s) => requiresOutdoorPigLiquidApplications(s))) delete offlineBudget.budget.outdoorPigEffluentSystem.liquidEffluentApplications;

                // Clean up liquid application months
                if (action.payload.structure.type === "OutdoorPigFarrowingVillages" && action.payload.structure.effluentDisposal !== "HoldingpondSprayinginfrequently") {
                    offlineBudget.budget.outdoorPigEffluentSystem.liquidEffluentApplications = (offlineBudget.budget.outdoorPigEffluentSystem.liquidEffluentApplications || []).map((application) => {
                        const isForCropBlocks = application.blockIds.some((blockId) => (offlineBudget.budget.blocks || []).some((b) => b.id === blockId && ["ProductiveCrop", "FodderCrop"].includes(b.type)));
                        if (!isForCropBlocks) return { ...application, months: [] };

                        return application;
                    });
                }

                if (!offlineBudget.budget.structures.some((s) => requiresOutdoorPigSolidApplications(s))) delete offlineBudget.budget.outdoorPigEffluentSystem.pondSolidApplications;

                if (!offlineBudget.budget.structures.some((s) => requiresOutdoorPigStrawCompostApplications(s))) delete offlineBudget.budget.outdoorPigEffluentSystem.strawCompostApplications;
            }
            return offlineFarms;
        }

        case actionTypes.EFFLUENT_STRUCTURE_DELETE: {
            const structureBeingDeleted = (offlineBudget.budget.structures || []).find((s) => s.id === action.payload.id);
            offlineBudget.budget.structures = offlineBudget.budget.structures.filter((s) => s.id !== structureBeingDeleted.id);
            offlineBudget.isDirty = true;
            // Clean up associated feed supplements.
            if (offlineBudget.budget.feedSupplements && offlineBudget.budget.feedSupplements.length > 0) {
                const supplements = offlineBudget.budget.feedSupplements.reduce((results, supplement) => {
                    const destinations = (supplement.destinations || []).filter((destination) => destination.structureId !== action.payload.id);
                    results.push({ ...supplement, destinations });
                    return results;
                }, []);
                offlineBudget.budget.feedSupplements = supplements;
            }
            // Clean up associated dairy goat left overs wintering pads.
            if (["CoveredWinteringPad", "UncoveredWinteringPad"].includes(structureBeingDeleted.type)) {
                offlineBudget.budget.enterprises = (offlineBudget.budget.enterprises || []).map((enterprise) => {
                    if (enterprise.type === "DairyGoat" && enterprise.dairyGoatLeftOvers && enterprise.dairyGoatLeftOvers.winteringPadLeftOvers) {
                        if (structureBeingDeleted.enterpriseId === enterprise.id) {
                            delete enterprise.dairyGoatLeftOvers.winteringPadLeftOvers;
                        } else if (enterprise.dairyGoatLeftOvers.winteringPadLeftOvers.fate === "FedOnWinteringPads") {
                            const enterprisePercentages = enterprise.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages.filter((ep) => ep.enterpriseId !== structureBeingDeleted.enterpriseId);
                            if (enterprisePercentages.length === 0) {
                                delete enterprise.dairyGoatLeftOvers.winteringPadLeftOvers;
                            } else {
                                enterprise.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages = enterprisePercentages;
                            }
                        }
                    }
                    return enterprise;
                });
            }
            // Clean up associated outdoor pigs effluent applications.
            if (offlineBudget.budget.outdoorPigEffluentSystem && ["OutdoorPigFarrowingVillages", "OutdoorPigBarns", "OutdoorPigHuts"].includes(structureBeingDeleted.type)) {
                if (!offlineBudget.budget.structures.some((s) => requiresOutdoorPigLiquidApplications(s))) delete offlineBudget.budget.outdoorPigEffluentSystem.liquidEffluentApplications;

                if (!offlineBudget.budget.structures.some((s) => requiresOutdoorPigSolidApplications(s))) delete offlineBudget.budget.outdoorPigEffluentSystem.pondSolidApplications;

                if (!offlineBudget.budget.structures.some((s) => requiresOutdoorPigStrawCompostApplications(s))) delete offlineBudget.budget.outdoorPigEffluentSystem.strawCompostApplications;
            }
            return offlineFarms;
        }
        default:
            return state;
    }
}

export default reducer;
